
import React, { useState } from 'react';
import { Send, Loader2, CheckCircle2, MessageSquare, User, Mail, Building2, Phone, Headphones, Sparkles } from 'lucide-react';

export const ContactSection = () => {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('sending');
    
    const formData = new FormData(e.currentTarget);
    
    try {
      const response = await fetch('https://formspree.io/f/placeholder', { 
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch (_error) {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-16 md:py-32 bg-white px-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-gray-50 rounded-[40px] md:rounded-[60px] overflow-hidden border border-gray-100 flex flex-col lg:flex-row shadow-[0_40px_80px_-20px_rgba(0,0,0,0.05)]">
          
          {/* Lado Esquerdo: Identidade Visual (1/3) */}
          <div className="lg:w-1/3 bg-primary p-10 md:p-16 text-white flex flex-col justify-between relative overflow-hidden shrink-0">
            <div className="absolute top-0 right-0 p-4 opacity-10 rotate-12 scale-150 pointer-events-none">
              <Headphones size={200} />
            </div>
            
            <div className="relative z-10 space-y-6">
              <div className="bg-white/20 w-fit p-3 md:p-4 rounded-2xl md:rounded-3xl backdrop-blur-md border border-white/20">
                <MessageSquare size={28} className="md:w-8 md:h-8" />
              </div>
              <div className="space-y-2">
                <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tighter italic leading-none">Fale com um <br /> Especialista</h2>
                <p className="text-[9px] md:text-[10px] font-black text-white/60 uppercase tracking-[0.3em] md:tracking-[0.4em]">Consultoria EventLog</p>
              </div>
            </div>

            <div className="relative z-10 pt-10 lg:pt-0 space-y-4">
              <div className="flex items-center gap-3">
                 <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse shadow-[0_0_8px_rgba(74,222,128,1)]"></div>
                 <span className="text-[9px] md:text-[10px] font-black uppercase tracking-widest text-white/80">Consultores Online</span>
              </div>
              <p className="text-xs font-medium text-white/70 leading-relaxed max-w-xs">
                Nossa equipe técnica está pronta para analisar sua operação e sugerir as melhores automações para seu estoque.
              </p>
            </div>
          </div>

          {/* Lado Direito: Formulário (2/3) */}
          <div className="lg:w-2/3 p-8 md:p-16 bg-white">
            {status === 'success' ? (
              <div className="h-full flex flex-col items-center justify-center text-center space-y-6 animate-in zoom-in">
                <div className="w-20 h-20 md:w-24 md:h-24 bg-green-50 text-green-500 rounded-[32px] md:rounded-[40px] flex items-center justify-center shadow-inner">
                  {/* Fix: Replaced invalid 'md:size' prop with responsive Tailwind width/height classes */}
                  <CheckCircle2 className="w-12 h-12 md:w-14 md:h-14 animate-bounce" />
                </div>
                <div>
                  <h3 className="text-2xl md:text-3xl font-black text-secondary uppercase tracking-tight italic">Solicitação Recebida!</h3>
                  <p className="text-gray-400 font-medium max-w-sm mx-auto mt-2 text-sm">Um de nossos especialistas entrará em contato via WhatsApp em poucos minutos.</p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                  <div className="space-y-2">
                    <label className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2 px-1"><User size={12} className="text-primary" /> Nome Completo</label>
                    <input required name="name" className="w-full bg-gray-50 border border-gray-100 p-4 md:p-5 rounded-xl md:rounded-2xl outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary font-bold text-secondary text-sm transition-all shadow-inner" placeholder="Seu nome" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2 px-1"><Mail size={12} className="text-primary" /> E-mail Profissional</label>
                    <input required name="email" type="email" className="w-full bg-gray-50 border border-gray-100 p-4 md:p-5 rounded-xl md:rounded-2xl outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary font-bold text-secondary text-sm transition-all shadow-inner" placeholder="exemplo@empresa.com" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                  <div className="space-y-2">
                    <label className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2 px-1"><Building2 size={12} className="text-primary" /> Locadora / Empresa</label>
                    <input required name="company" className="w-full bg-gray-50 border border-gray-100 p-4 md:p-5 rounded-xl md:rounded-2xl outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary font-bold text-secondary text-sm transition-all shadow-inner" placeholder="Nome da empresa" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2 px-1"><Phone size={12} className="text-primary" /> WhatsApp</label>
                    <input required name="phone" className="w-full bg-gray-50 border border-gray-100 p-4 md:p-5 rounded-xl md:rounded-2xl outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary font-bold text-secondary text-sm transition-all shadow-inner" placeholder="(00) 00000-0000" />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2 px-1"><Sparkles size={12} className="text-primary" /> Desafios da Operação</label>
                  <textarea required name="message" rows={3} className="w-full bg-gray-50 border border-gray-100 p-5 md:p-6 rounded-[24px] md:rounded-3xl outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary font-bold text-secondary text-sm transition-all resize-none shadow-inner" placeholder="O que você precisa automatizar hoje?"></textarea>
                </div>

                {status === 'error' && (
                  <div className="bg-red-50 text-red-500 p-4 rounded-xl text-[9px] font-black uppercase text-center border border-red-100">
                    Erro no servidor. Tente novamente.
                  </div>
                )}

                <button 
                  disabled={status === 'sending'}
                  type="submit" 
                  className="w-full lg:w-fit px-10 md:px-12 bg-primary text-white py-4 md:py-6 rounded-[24px] md:rounded-[32px] font-black uppercase text-[10px] md:text-xs tracking-[0.2em] md:tracking-[0.3em] shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-4 disabled:opacity-50"
                >
                  {status === 'sending' ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} />}
                  {status === 'sending' ? 'Processando...' : 'Agendar Demonstração'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
